import {
  type PropsWithChildren,
  type Dispatch,
  type SetStateAction,
  useState,
  useEffect,
} from 'react';
import { useIntl } from 'react-intl';
import { MenuTrigger, Popover } from 'react-aria-components';
import cx from 'clsx';
import { useSegment } from '@repo/poly-hooks/segment';
import { FilterButton } from '@repo/design-system-kit';
import type { OperatorKey, PropertyOption } from '../../../types';
import styles from './filters-list-item.module.css';

const SELECT_FILTER_TRACKING_EVENT = 'filter_selected';
const DELETE_FILTER_TRACKING_EVENT = 'delete_filter';

export interface FiltersListItemProps {
  autoOpen?: boolean;
  values?: string[];
  isHidden?: boolean;
  onOpen?: () => void;
  onClose?: (filter?: { operator: OperatorKey; values: string[] }) => void;
}

export interface FilterListItemComponentProps {
  filter: PropertyOption;
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  formattedLabel?: string;
  advancedFilterCount?: number;
  isAdvancedFilter?: boolean;
  isActive?: boolean;
  isHidden?: boolean;

  onOpen?: () => void;
  onClose?: () => void;
  onClear?: () => void;
}

export function FiltersListItem({
  filter,
  open,
  setOpen,
  formattedLabel,
  isActive = false,
  advancedFilterCount = 0,
  isAdvancedFilter = false,
  isHidden = false,
  onClose,
  onOpen,
  children,
  onClear,
}: PropsWithChildren<FilterListItemComponentProps>) {
  const segment = useSegment();
  const { formatMessage } = useIntl();

  // Allow position to compute on first render, then lock it so it doesn't jump when the trigger button hides.
  const [shouldUpdate, setShouldUpdate] = useState(true);

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) {
      setShouldUpdate(true);
      onClose?.();
      return;
    }

    if (!isActive) {
      segment.track(SELECT_FILTER_TRACKING_EVENT, {
        filter_name: filter.key,
      });
    }

    onOpen?.();
  }

  useEffect(() => {
    if (open && shouldUpdate) {
      requestAnimationFrame(() => {
        setShouldUpdate(false);
      });
    }
  }, [open, shouldUpdate]);

  useEffect(() => {
    if (!isHidden) {
      setShouldUpdate(true);
    }
  }, [isHidden]);

  return (
    <MenuTrigger isOpen={open} onOpenChange={handleOpenChange}>
      <FilterButton
        className={cx(styles.filterButton, isHidden && styles.hiddenFilterButton)}
        data-filter-key={filter.key}
        data-testid="filter-button"
        hasMenu
        isActive={isActive}
        key={filter.key}
        onClear={() => {
          onClear?.();
          segment.track(DELETE_FILTER_TRACKING_EVENT, {
            filter_name: filter.key,
          });
        }}
      >
        {isAdvancedFilter ? (
          <span className={styles.filterButtonLabel}>
            {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx --- space after message*/}
            {`${advancedFilterCount} ${formatMessage(
              { id: 'shared-kit.filters.advanced-filter.label' },
              { count: advancedFilterCount }
            )}`}
          </span>
        ) : (
          <span className={styles.filterButtonLabel}>{formattedLabel ?? filter.label}</span>
        )}
      </FilterButton>
      <Popover className={styles.dropdown} shouldUpdatePosition={shouldUpdate}>
        {children}
      </Popover>
    </MenuTrigger>
  );
}
